import { reactive } from 'vue';
import TranscriptService from '@/services/transcript.service';

// Summary state per session, kept at module level so a generation keeps running
// while the user reads another session or leaves the page and comes back.
//
// status: 'idle' | 'generating' | 'ready' | 'error'
// step (while generating): 'transcript_ready' | 'highlights' | 'questions'
const states = reactive({});
const controllers = new Map();

function summaryState(id) {
  if (!states[id]) {
    states[id] = { status: 'idle', step: null, summary: null, feedback: null, loaded: false };
  }
  return states[id];
}

async function loadSummary(id) {
  const state = summaryState(id);
  if (state.loaded || state.status === 'generating') return;

  const { data } = await TranscriptService.getTranscriptSummaryMock(id);
  const { summary } = data.result;
  state.loaded = true;
  if (summary && state.status !== 'generating') {
    state.summary = summary;
    state.feedback = summary.feedback || null;
    state.status = 'ready';
  }
}

async function generateSummary(id) {
  const state = summaryState(id);
  if (state.status === 'generating') return;

  const controller = new AbortController();
  controllers.set(id, controller);
  state.status = 'generating';
  state.step = null;

  try {
    const { data } = await TranscriptService.generateTranscriptSummaryMock(id, {
      signal: controller.signal,
      onProgress: (step) => { state.step = step; },
    });
    state.summary = data.result.summary;
    state.feedback = null;
    state.status = 'ready';
  } catch (error) {
    state.status = error.name === 'AbortError' ? 'idle' : 'error';
  } finally {
    state.step = null;
    controllers.delete(id);
  }
}

function cancelSummary(id) {
  controllers.get(id)?.abort();
}

async function setSummaryFeedback(id, value) {
  const state = summaryState(id);
  const previous = state.feedback;
  state.feedback = state.feedback === value ? null : value;
  try {
    await TranscriptService.sendTranscriptSummaryFeedbackMock(id, state.feedback);
  } catch (error) {
    state.feedback = previous;
    throw error;
  }
}

export function useTranscriptSummary() {
  return { summaryState, loadSummary, generateSummary, cancelSummary, setSummaryFeedback };
}
